import {type ChartSeries} from '../../vis/chartSeries';
import {type GraphProps} from '../GraphAdapter';
import {useLinePlotZoomUsageTracker} from '../observability/zoomUsage';
import {getPointScaleFilter} from '../useLinesForDisplay';
import {
  getGraphZoom,
  type GraphZoom,
  type GraphZoomSettings,
} from './getGraphZoom';

/** Select zoom and application effects from neutral numeric snapshots before rendering. */
export function usePreparedGraphZoom(
  {
    config,
    panelId,
    plotSettings,
    zoom,
    zoomUsage,
  }: Pick<
    GraphProps,
    'config' | 'panelId' | 'plotSettings' | 'zoom' | 'zoomUsage'
  >,
  series: ChartSeries[]
): GraphZoom {
  // Configured metrics win. The fallback checks visibility without allocating
  // filtered point arrays or resolving a second numeric-line projection.
  const shouldKeep = getPointScaleFilter(
    plotSettings.xLogScale ? 'log' : 'linear',
    plotSettings.yLogScale ? 'log' : 'linear',
    plotSettings.isFullFidelity
  );
  // Without configured metrics, report series with a point passing the scale filter or a NaN marker.
  const visibleSeries =
    config.metrics != null && config.metrics.length > 0
      ? series
      : series.filter(
          item =>
            (item.nanPoints?.length ?? 0) > 0 ||
            item.historySeries.some(shouldKeep)
        );
  const trackZoomUsage = useLinePlotZoomUsageTracker({
    ...zoomUsage,
    lines: visibleSeries,
    metrics: config.metrics,
    panelId,
    xAxis: plotSettings.xAxis,
  });
  const settings: GraphZoomSettings = {
    isFullFidelity: plotSettings.isFullFidelity,
    xAxis: plotSettings.xAxis,
    xAxisIsDatetime: plotSettings.xAxisIsDatetime,
  };
  return getGraphZoom(zoom, settings, series[0]?.timestep, trackZoomUsage);
}
